'use client'
import { useStatsigClient } from '@statsig/react-bindings'
import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react'

import {
  STATSIG_EXPERIMENTS,
  STATSIG_EXPERIMENTS_BUCKETS,
  STATSIG_EXPERIMENTS_CONFIG,
} from '@src/config/statsig-experiments.config'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { setElementVisibilityIntersectionObserverEnabled } from '@src/quince-site-common/src/hooks/element-visibility-observer.experiment'
import { setStatsigLogEvent } from '@src/telemetry/datadog-rum-before-send'
import { setTelemetryFanOutDeferralEnabled } from '@src/telemetry/telemetry-fan-out-scheduler'
import { isNonNullish } from '@src/utils/functions/utils'

import { IExperimentLoggingData } from '../experiments.types'

import { statsigExperimentManager } from './statsig-experiment.manager'
import { StatsigInitContext } from './statsig.context'
import { StatsigExperimentBucket } from './statsig.types'

export function StatsigUserAwareProvider({
  children,
  serverSideExperiments,
  pageOptions,
}: PropsWithChildren<{
  serverSideExperiments: StatsigExperimentBucket[]
  pageOptions: {
    pageType: string | undefined
  }
}>) {
  const client = useStatsigClient()
  const { isB2BMode } = useStorefrontModeContext()
  const isNativeWebView = getIsNativeViewClient()

  const getExperimentBucket = useCallback(
    (experimentName: STATSIG_EXPERIMENTS | null) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!experimentName || !STATSIG_EXPERIMENTS_CONFIG[experimentName]) return null
      const fallback = STATSIG_EXPERIMENTS_CONFIG[experimentName].defaultBucket

      if (isB2BMode) return STATSIG_EXPERIMENTS_CONFIG[experimentName].defaultB2BBucket ?? fallback

      // In webview the SDK loads after the page, so asking now logs an exposure with no bucket.
      // Web is not affected because its SDK is already ready from the server.
      if (isNativeWebView && client.client.loadingStatus !== 'Ready') return fallback

      try {
        const experimentConfig = client.getExperiment(experimentName)
        if (experimentConfig.groupName) {
          statsigExperimentManager.setExposedBuckets(
            [
              {
                experimentName,
                bucketName: experimentConfig.groupName,
                isActive: experimentConfig.__evaluation?.is_experiment_active ?? false,
              },
            ],
            false,
          )
        }
        return experimentConfig.groupName ?? fallback
      } catch {
        return fallback
      }
    },
    [client, isB2BMode, isNativeWebView],
  )
  useEffect(() => {
    if (client.isLoading) return
    statsigExperimentManager.initialize() // load from local storage
    const experimentNames = [...serverSideExperiments, ...statsigExperimentManager.getExposedBuckets()].map(
      u => u.experimentName,
    )
    const serverSideExperimentNames = new Set(serverSideExperiments.map(exp => exp.experimentName))
    const buckets = experimentNames.map((experimentName: string): IExperimentLoggingData | null => {
      const isKnownExperiment =
        experimentName &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (STATSIG_EXPERIMENTS_CONFIG[experimentName as STATSIG_EXPERIMENTS] ||
          serverSideExperiments.some(exp => exp.experimentName === experimentName))

      if (!isKnownExperiment) {
        return null
      }
      // disable exposure only for client side experiments, but log exposure for server side experiments
      const experimentConfig = client.getExperiment(experimentName, {
        disableExposureLog: !serverSideExperimentNames.has(experimentName),
      })
      if (!experimentConfig.groupName) {
        return null
      }

      return {
        experimentName,
        bucketName: experimentConfig.groupName,
        isActive: experimentConfig.__evaluation?.is_experiment_active ?? false,
      }
    })

    const convertedBuckets = buckets.filter(isNonNullish)
    statsigExperimentManager.setExposedBuckets(convertedBuckets, true)
  }, [client, serverSideExperiments])

  useEffect(() => {
    if (client.isLoading) return
    client.logEvent('page_view', pageOptions.pageType)
    setStatsigLogEvent((eventName, value) => {
      client.logEvent(eventName, value)
    })
    return () => {
      setStatsigLogEvent(undefined)
    }
  }, [client])

  // Telemetry fan-out deferral is experiment-gated the same way: read the bucket once it resolves
  // and cache it in the scheduler module, so `track()` never calls Statsig per event. Only the Test
  // bucket defers; Control keeps the historical synchronous fan-out. `getExperimentBucket` already
  // falls back to the configured default bucket if the lookup throws.
  useEffect(() => {
    if (client.isLoading) return
    setTelemetryFanOutDeferralEnabled(
      getExperimentBucket(STATSIG_EXPERIMENTS.TELEMETRY_FAN_OUT_DEFER) === STATSIG_EXPERIMENTS_BUCKETS.TEST_CAP,
    )
  }, [client, getExperimentBucket])

  // Element-visibility observation is experiment-gated the same way: read the bucket once it
  // resolves and push it into the module cache the hook subscribes to, so ~125 mounted consumers
  // never call Statsig each. Only Test uses the IntersectionObserver; Control keeps the historical
  // document scroll listener. Consumers that mounted before this resolves are re-rendered by the
  // subscription, so above-the-fold ones are not stranded on Control.
  useEffect(() => {
    if (client.isLoading) return
    setElementVisibilityIntersectionObserverEnabled(
      getExperimentBucket(STATSIG_EXPERIMENTS.ELEMENT_VISIBILITY_INTERSECTION_OBSERVER) ===
        STATSIG_EXPERIMENTS_BUCKETS.TEST_CAP,
    )
  }, [client, getExperimentBucket])

  const value = useMemo(
    () => ({ init: client.client.loadingStatus === 'Ready', getExperimentBucket }),
    [client.client.loadingStatus, getExperimentBucket],
  )
  return <StatsigInitContext.Provider value={value}>{children}</StatsigInitContext.Provider>
}
